// `import type` is erased at compile-time — webpack never tries to bundle pptxgenjs.
// The actual runtime comes from the CDN script tag in public/index.html,
// which exposes window.PptxGenJS as a top-level var.
import type PptxGenJSType from "pptxgenjs";
import { InsightsContext } from "../redux/services/insightsApi";

function getPptx(): typeof PptxGenJSType {
  const ctor = (window as any).PptxGenJS;
  if (!ctor) throw new Error("PptxGenJS not loaded. Check the CDN script in index.html.");
  return ctor;
}

// ── colour palette ────────────────────────────────────────────────────────────
const C = {
  blue:   "143887",
  white:  "FFFFFF",
  gray:   "6B7280",
  lgray:  "E5E7EB",
  lblue:  "EBF1FB",
  green:  "059669",
  red:    "DC2626",
  amber:  "D97706",
  purple: "7C3AED",
  sky:    "A8C4F0",
  muted:  "C5D8F7",
  faded:  "7BA9DC",
};

const TITLE = {
  x: 0.4, y: 0.18, w: 12.5, h: 0.7,
  fontSize: 22, bold: true, color: C.blue, fontFace: "Calibri",
};

const SUB = {
  x: 0.4, y: 0.82, w: 12.5, h: 0.32,
  fontSize: 10, color: C.gray, fontFace: "Calibri",
};

function fmt(n: number): string {
  return n.toLocaleString("en-NG");
}

function fmtMonth(ym: string): string {
  const [y, m] = ym.split("-");
  return new Date(+y, +m - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
export async function generateInsightsPPTX(
  data: InsightsContext,
  companyName: string
): Promise<void> {
  const PptxGenJS = getPptx();
  const pptx = new (PptxGenJS as any)();
  pptx.layout = "LAYOUT_WIDE"; // 13.33 × 7.5 inches

  const period =
    data.period.from && data.period.to
      ? `${data.period.from}  →  ${data.period.to}`
      : "All time";

  // ── SLIDE 1 · COVER ──────────────────────────────────────────────────────
  {
    const s = pptx.addSlide();
    s.background = { color: C.blue };

    s.addShape("rect", {
      x: 0, y: 0, w: 13.33, h: 0.12,
      fill: { color: C.sky },
      line: { color: C.sky, width: 0 },
    });

    s.addText(companyName.toUpperCase(), {
      x: 1, y: 1.5, w: 11.33, h: 1.4,
      fontSize: 46, bold: true, color: C.white,
      align: "center", fontFace: "Calibri",
    });

    s.addText("AI Fleet Insights Report", {
      x: 1, y: 3.1, w: 11.33, h: 0.75,
      fontSize: 26, color: C.sky,
      align: "center", fontFace: "Calibri",
    });

    s.addText(period, {
      x: 1, y: 4.0, w: 11.33, h: 0.45,
      fontSize: 14, color: C.muted,
      align: "center",
    });

    s.addShape("rect", {
      x: 3.67, y: 4.65, w: 6, h: 0.04,
      fill: { color: C.sky },
      line: { color: C.sky, width: 0 },
    });

    s.addText("Powered by Fleetrify", {
      x: 1, y: 6.75, w: 11.33, h: 0.38,
      fontSize: 11, color: C.faded,
      align: "center", italic: true,
    });
  }

  // ── SLIDE 2 · KEY METRICS ────────────────────────────────────────────────
  {
    const s = pptx.addSlide();
    s.addText("Key Metrics Overview", TITLE);
    s.addText(period, SUB);

    const { overall: o } = data;
    const cards = [
      { label: "Total Orders",      value: fmt(o.total_orders),                  accent: C.blue  },
      { label: "Completed Orders",  value: fmt(o.completed_orders),               accent: C.green },
      { label: "Cancelled Orders",  value: fmt(o.cancelled_orders),               accent: C.red   },
      { label: "Pending Orders",    value: fmt(o.pending_orders),                 accent: C.amber },
      { label: "Total Revenue",     value: `₦${fmt(o.total_revenue)}`,            accent: C.blue  },
      { label: "Uncollected Rev.",  value: `₦${fmt(o.uncollected_revenue ?? 0)}`, accent: C.red   },
      { label: "Completion Rate",   value: `${o.completion_rate}%`,               accent: C.green },
      { label: "Cancellation Rate", value: `${o.cancellation_rate}%`,             accent: C.red   },
    ];

    cards.forEach((c, i) => {
      const col = i % 4;
      const row = Math.floor(i / 4);
      const x = 0.3 + col * 3.25;
      const y = 1.28 + row * 2.6;

      s.addShape("rect", {
        x, y, w: 3.05, h: 2.35,
        fill: { color: C.lblue },
        line: { color: c.accent, width: 2 },
      });
      s.addShape("rect", {
        x, y, w: 3.05, h: 0.09,
        fill: { color: c.accent },
        line: { color: c.accent, width: 0 },
      });
      s.addText(c.label, {
        x: x + 0.15, y: y + 0.22, w: 2.78, h: 0.44,
        fontSize: 11, color: C.gray, fontFace: "Calibri",
      });
      s.addText(c.value, {
        x: x + 0.15, y: y + 0.85, w: 2.78, h: 1.1,
        fontSize: 21, bold: true, color: c.accent, fontFace: "Calibri",
      });
    });
  }

  // ── SLIDE 3 · ORDER STATUS PIE ───────────────────────────────────────────
  {
    const s = pptx.addSlide();
    s.addText("Order Status Distribution", TITLE);
    s.addText(period, SUB);

    const { overall: o } = data;
    s.addChart("pie", [
      {
        name: "Order Status",
        labels: ["Completed", "Cancelled", "Pending"],
        values: [o.completed_orders, o.cancelled_orders, o.pending_orders],
      },
    ], {
      x: 2, y: 1.2, w: 9, h: 5.6,
      chartColors: [C.green, C.red, C.amber],
      showLegend: true,
      legendPos: "b",
      legendFontSize: 13,
      showValue: true,
      dataLabelFontSize: 13,
    });
  }

  // ── SLIDE 4 · MONTHLY REVENUE ────────────────────────────────────────────
  if (data.monthly_summary?.length > 0) {
    const s = pptx.addSlide();
    s.addText("Monthly Revenue", TITLE);
    s.addText(period, SUB);

    s.addChart("bar", [
      {
        name: "Revenue (₦)",
        labels: data.monthly_summary.map((m) => fmtMonth(m.month)),
        values: data.monthly_summary.map((m) => m.revenue),
      },
    ], {
      x: 0.4, y: 1.2, w: 12.5, h: 5.6,
      barDir: "col",
      chartColors: [C.blue],
      showValue: true,
      dataLabelFontSize: 9,
      showLegend: false,
      catAxisLabelFontSize: 11,
      valAxisNumFmt: "#,##0",
    });
  }

  // ── SLIDE 5 · MONTHLY ORDERS ─────────────────────────────────────────────
  if (data.monthly_summary?.length > 0) {
    const s = pptx.addSlide();
    s.addText("Monthly Orders", TITLE);
    s.addText(period, SUB);

    s.addChart("bar", [
      {
        name: "Total Orders",
        labels: data.monthly_summary.map((m) => fmtMonth(m.month)),
        values: data.monthly_summary.map((m) => m.total_orders),
      },
      {
        name: "Completed",
        labels: data.monthly_summary.map((m) => fmtMonth(m.month)),
        values: data.monthly_summary.map((m) => m.completed),
      },
      {
        name: "Cancelled",
        labels: data.monthly_summary.map((m) => fmtMonth(m.month)),
        values: data.monthly_summary.map((m) => m.cancelled),
      },
    ], {
      x: 0.4, y: 1.2, w: 12.5, h: 5.6,
      barDir: "col",
      barGrouping: "clustered",
      chartColors: [C.blue, C.green, C.red],
      showLegend: true,
      legendPos: "b",
      legendFontSize: 11,
      catAxisLabelFontSize: 11,
    });
  }

  // ── SLIDE 6 · REVENUE BY SERVICE ─────────────────────────────────────────
  if (data.revenue_by_service?.length > 0) {
    const s = pptx.addSlide();
    s.addText("Revenue by Service", TITLE);
    s.addText(period, SUB);

    s.addChart("bar", [
      {
        name: "Revenue (₦)",
        labels: data.revenue_by_service.map((r) => r.service_name),
        values: data.revenue_by_service.map((r) => r.revenue),
      },
    ], {
      x: 0.4, y: 1.2, w: 12.5, h: 5.6,
      barDir: "bar",
      chartColors: [C.purple],
      showValue: true,
      dataLabelFontSize: 10,
      valAxisNumFmt: "#,##0",
      catAxisLabelFontSize: 11,
    });
  }

  // ── SLIDE 7 · MOST REQUESTED SERVICES ───────────────────────────────────
  if (data.most_requested?.length > 0) {
    const s = pptx.addSlide();
    s.addText("Most Requested Services", TITLE);
    s.addText(period, SUB);

    s.addChart("bar", [
      {
        name: "Total Orders",
        labels: data.most_requested.map((r) => r.service_name),
        values: data.most_requested.map((r) => r.total_orders),
      },
    ], {
      x: 0.4, y: 1.2, w: 12.5, h: 5.6,
      barDir: "bar",
      chartColors: [C.amber],
      showValue: true,
      dataLabelFontSize: 10,
      catAxisLabelFontSize: 11,
    });
  }

  // ── SLIDE 8 · TOP DRIVERS TABLE ──────────────────────────────────────────
  if (data.top_drivers?.length > 0) {
    const s = pptx.addSlide();
    s.addText("Top Performing Drivers", TITLE);
    s.addText(period, SUB);

    const hOpts = {
      bold: true, color: C.white, fill: { color: C.blue },
      align: "center" as const, fontFace: "Calibri", fontSize: 13,
    };

    const rows = [
      [
        { text: "#",              options: hOpts },
        { text: "Driver Name",    options: { ...hOpts, align: "left" as const } },
        { text: "Completed Jobs", options: hOpts },
        { text: "Avg Rating",     options: hOpts },
      ],
      ...data.top_drivers.map((d, i) => {
        const bg = i % 2 === 0 ? C.lblue : C.white;
        const base = { fill: { color: bg }, fontFace: "Calibri", fontSize: 12 };
        return [
          { text: String(i + 1),                   options: { ...base, align: "center" as const } },
          { text: d.driver_name,                    options: { ...base, align: "left" as const } },
          { text: String(d.completed_jobs),         options: { ...base, align: "center" as const } },
          { text: d.avg_rating != null ? `${d.avg_rating.toFixed(1)} ★` : "No ratings",  options: { ...base, align: "center" as const, color: C.amber } },
        ];
      }),
    ];

    s.addTable(rows, {
      x: 0.8, y: 1.35, w: 11.73,
      colW: [0.8, 5.93, 2.5, 2.5],
      fontSize: 12,
      border: { type: "solid", color: C.lgray, pt: 1 },
      rowH: 0.52,
    });
  }

  // ── SLIDE 9 · CANCELLATION TRENDS ───────────────────────────────────────
  if (data.cancellation_trends?.length > 0) {
    const s = pptx.addSlide();
    s.addText("Cancellation Rate Trends", TITLE);
    s.addText(period, SUB);

    s.addChart("line", [
      {
        name: "Cancellation Rate (%)",
        labels: data.cancellation_trends.map((t) => fmtMonth(t.month)),
        values: data.cancellation_trends.map((t) => t.cancellation_rate),
      },
    ], {
      x: 0.4, y: 1.2, w: 12.5, h: 5.6,
      chartColors: [C.red],
      lineSize: 2.5,
      showMarker: true,
      showValue: true,
      dataLabelFontSize: 10,
      catAxisLabelFontSize: 11,
      valAxisNumFmt: "0.0",
    });
  }

  // ── DOWNLOAD ──────────────────────────────────────────────────────────────
  const today = new Date().toISOString().split("T")[0];
  const safe = companyName.replace(/[^a-zA-Z0-9]/g, "-").replace(/-+/g, "-");
  await pptx.writeFile({ fileName: `${safe}-AI-Insights-${today}.pptx` });
}
